import * as React from "react"

import { cn } from "@/lib/utils"
import { GregorianDatePicker } from "@/components/ui/gregorian-date-picker"
import { GregorianDateTimePicker } from "@/components/ui/gregorian-datetime-picker"

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, min, value, onChange, disabled, ...props }, ref) => {
    // Global Hijri → Gregorian override. Safari / Chrome / Edge on
    // Saudi-locale systems ignore the `lang` attribute on native date
    // inputs and force a Hijri popup calendar. Every <Input
    // type="date" /> in the platform is silently routed through our
    // Gregorian picker so merchants never see a Hijri popup.
    // Callers that need the native picker can use <input> directly.
    if (type === "date" || type === "datetime-local") {
      const stringValue = typeof value === "string" ? value : value == null ? "" : String(value)
      const emit = (v: string) => {
        if (!onChange) return
        // Synthesize an event-like object so existing callers that
        // read `e.target.value` keep working. React Hook Form's
        // `field.onChange` also accepts this shape.
        onChange({
          target: { value: v, name: (props as any).name },
          currentTarget: { value: v, name: (props as any).name },
        } as unknown as React.ChangeEvent<HTMLInputElement>)
      }
      if (type === "date") {
        return (
          <GregorianDatePicker
            value={stringValue}
            onChange={emit}
            disabled={disabled}
            className={className}
            testId={(props as any)["data-testid"]}
          />
        )
      }
      return (
        <GregorianDateTimePicker
          value={stringValue}
          onChange={emit}
          disabled={disabled}
          className={className}
          testId={(props as any)["data-testid"]}
        />
      )
    }

    // Default min="0" for number inputs unless the caller explicitly
    // sets a min (including negatives). Nothing in the platform admin
    // or storefront should accept negative quantities, prices, or
    // counts — merchants kept entering things like -2 for "featured
    // order" or -0.02 as a discount value, which then propagated to
    // the storefront. Pass min={-1} to opt out.
    const resolvedMin = type === "number" && min === undefined ? 0 : min
    return (
      <input
        type={type}
        min={resolvedMin}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
